import { useEffect, useState } from "react";
import { FileDown, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export const downloadGdprExport = async (clientId, format) => {
  try {
    const res = await api.get(`/clients/${clientId}/gdpr-export`, { params: { format }, responseType: "blob" });
    const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || `dati_gdpr.${format}`;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) { toast.error(formatApiError(e)); }
};

const fmtTs = (iso) => (iso ? `${formatDate(iso)} ${iso.slice(11, 16)}` : "—");

// Pannello "Privacy e consensi" nella scheda cliente.
export const ClientGdprPanel = ({ clientId, canExport }) => {
  const [info, setInfo] = useState(null);
  const [vals, setVals] = useState({ trattamento: false, marketing: false, terzi: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/clients/${clientId}/gdpr`).then(({ data }) => {
      setInfo(data);
      const c = data.consents || {};
      setVals({ trattamento: !!c.trattamento, marketing: !!c.marketing, terzi: !!c.terzi });
    }).catch(() => setInfo({ consents: null, history: [] }));
  }, [clientId]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/clients/${clientId}/gdpr-consents`, vals);
      setInfo((i) => ({ ...i, consents: data, history: [data, ...(i?.history || [])] }));
      toast.success("Consensi privacy registrati");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const c = info?.consents;
  const items = [["trattamento", "Trattamento dati (obbligatorio)"], ["marketing", "Marketing"], ["terzi", "Cessione a terzi"]];
  return (
    <div className="col-span-full mt-4 border border-emerald-200 bg-emerald-50/40 rounded-lg p-3" data-testid="client-gdpr-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-semibold text-sm flex items-center gap-1.5 text-emerald-900"><ShieldCheck className="h-4 w-4" /> Privacy e consensi (GDPR)</div>
        {canExport && (
          <div className="flex gap-1.5">
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadGdprExport(clientId, "pdf")} data-testid="client-gdpr-export-pdf"><FileDown className="h-3.5 w-3.5 mr-1" />Export PDF</Button>
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadGdprExport(clientId, "csv")} data-testid="client-gdpr-export-csv"><FileDown className="h-3.5 w-3.5 mr-1" />Export CSV</Button>
          </div>
        )}
      </div>
      {!info ? <Loader2 className="h-4 w-4 animate-spin text-slate-400 mt-2" /> : (
        <>
          <div className="flex flex-wrap gap-4 mt-2">
            {items.map(([k, l]) => (
              <label key={k} className="flex items-center gap-1.5 text-xs cursor-pointer">
                <Checkbox checked={vals[k]} onCheckedChange={(v) => setVals({ ...vals, [k]: !!v })} data-testid={`client-gdpr-${k}`} /> {l}
              </label>
            ))}
            <Button type="button" size="sm" className="h-7 text-xs ml-auto" onClick={save} disabled={saving} data-testid="client-gdpr-save">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />} Registra consensi
            </Button>
          </div>
          <div className="text-[11px] text-slate-600 mt-1.5" data-testid="client-gdpr-last">
            {c ? <>Ultima registrazione: <b>{fmtTs(c.at)}</b> · informativa v{c.privacy_version} · {c.source === "area_riservata" ? "accettato dal cliente in area riservata" : `registrato da ${c.by}`} · {info.history.length} registrazioni</>
              : <span className="text-amber-700 font-medium">Nessun consenso registrato per questo cliente.</span>}
          </div>
        </>
      )}
    </div>
  );
};
